import { useCallback, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { Dataset, DtCursor, DtFilter, DtPage, DtSort } from '@/components/data-table/helpers';
import { BaseModel } from '@/lib/models/base.model';

type ResetSnapshot = {
  pageSize: number;
  sort?: DtSort;
  filter?: DtFilter;
};

const buildSnapshot = (pageParams: DtPage, filter?: DtFilter): ResetSnapshot => ({
  pageSize: pageParams.pageSize,
  sort: pageParams.sort,
  filter,
});

type CursorPagination = {
  getNextCursor: (pageParams: DtPage, filter?: DtFilter) => string;
  updateLastCursor: (result: Dataset<BaseModel>) => void;
  getCursorTotal: (pageParams: DtPage) => number;
};

/**
 * Bridges MUI/DataTable page index with a cursor-based API: keeps last next/previous/current
 * cursors and derives which cursor string to send per request (including reset on filter/sort/pageSize).
 */
const useDataTableCursor = (): CursorPagination => {
  const lastCursorRef = useRef<DtCursor | null>(null);
  const priorPageRef = useRef<DtPage | null>(null);
  const snapshotRef = useRef<ResetSnapshot | null>(null);

  const getNextCursor = useCallback((pageParams: DtPage, filter?: DtFilter): string => {
    const nextSnapshot = buildSnapshot(pageParams, filter);
    const snapshotChanged =
      snapshotRef.current === null || !isEqual(snapshotRef.current, nextSnapshot);

    if (snapshotChanged) {
      lastCursorRef.current = null;
      priorPageRef.current = pageParams;
      snapshotRef.current = nextSnapshot;
      return '';
    }

    if (!priorPageRef.current) {
      priorPageRef.current = pageParams;
      return '';
    }

    const priorPage = priorPageRef.current.page;
    const currentPage = pageParams.page;
    const last = lastCursorRef.current;

    if (currentPage === priorPage) {
      return last?.current ?? '';
    }

    if (currentPage > priorPage) {
      priorPageRef.current = pageParams;
      return last?.next ?? '';
    }

    if (currentPage < priorPage) {
      priorPageRef.current = pageParams;
      return last?.previous ?? '';
    }

    return '';
  }, []);

  const getCursorTotal = useCallback((pageParams: DtPage): number => {
    const { pageSize, page } = pageParams;
    return lastCursorRef.current?.next ? (page + 1) * pageSize + 1 : (page + 1) * pageSize;
  }, []);

  const updateLastCursor = useCallback((result: Dataset<BaseModel>) => {
    const { cursor } = result;

    if (cursor) {
      lastCursorRef.current = cursor;
    }
  }, []);

  return { getNextCursor, updateLastCursor, getCursorTotal };
};

export default useDataTableCursor;
